import { FC, useState } from 'react'
import { Col, Table } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../context/auth-context'
import { transformToContractChangeObject, EMPTY_CHANGE } from './types'
import {
  LatestContractDtoFragment,
  Change,
  CareerPathEvaluation,
  CareerPathDto,
  FullMeetingFragment,
  GovernmentJobTitle,
  GovernmentJobTitleTinyDto,
  CountrySettingsFragment,
} from '../../../types'
import { useDateTime } from '../../../shared/useDateTime'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { ContractChangeList } from './ContractChangeList'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import PerButton from '../../../components/custom_components/Button'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { ContractChangeModalWithValidationList } from './ContractChangeModalWithValidationList'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'
import PerSpacer from '../../../components/custom_components/Spacer'
import { isGovernmentJobTitleValid, transformFromStringToGovernmentJobTitle } from './helpers'

interface Props {
  meeting: FullMeetingFragment
  type: 'contractChange' | 'conclusion'
  tableData: Pick<Change, 'id' | 'validFrom' | 'changes' | 'status' | 'type' | 'updatedDate'>[]
  currentContract: LatestContractDtoFragment
  careerPathData?: CareerPathDto[] | null
  careerPathEvaluation?: CareerPathEvaluation | null
  isConclusion?: boolean
  isPreparation?: boolean
  countrySettings: CountrySettingsFragment
  governmentJobTitles?: GovernmentJobTitle[]
}

export const ContractChangeTable: FC<Props> = ({
  meeting,
  type,
  tableData,
  currentContract,
  careerPathData,
  careerPathEvaluation,
  isConclusion = false,
  isPreparation = false,
  countrySettings,
  governmentJobTitles,
}) => {
  const handleLowTag = meeting.requiresSpecialHandlingOfLowTag
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDateShort, utcDateStringToMoment } = useDateTime(user)
  const [visible, setVisible] = useState(false)
  const [isEdit, toggleEdit] = useState(false)
  const [change, setChange] = useState(EMPTY_CHANGE)
  const [editedDate, setEditedDate] = useState(undefined)
  const [isDeleting, toggleDeleting] = useState<boolean>(false)

  const hasNationalContract = countrySettings.hasNationalContractReferenceLevel

  const { tableActionButtonsRowWidth } = tableActionButtons()

  const isContractChange = type === 'contractChange'

  const datesOfCurrentChanges = tableData.map((c) => new Date(c.validFrom))

  const scheduledChanges = tableData.map((change) => change.changes).toString()

  let noMoreUpdatesPossible = false
  // The target bonus is still allowed even before the restrictedTo date
  if (currentContract && currentContract.updatesRestrictedTo && scheduledChanges.includes('bonus')) {
    const currentYear = new Date().getUTCFullYear()
    const currentMonth = new Date().getUTCMonth()
    const restrictedTo = utcDateStringToMoment(currentContract.updatesRestrictedTo)
    if (
      restrictedTo &&
      ((restrictedTo.year() > currentYear && (currentMonth < 9 || restrictedTo.month() > 2)) ||
        (restrictedTo.year() === currentYear && restrictedTo.month() === 11 && currentMonth < 9))
    ) {
      noMoreUpdatesPossible = true
    }
  }

  const closeModal = () => {
    setVisible(false)
    toggleEdit(false)
    toggleDeleting(false)
    setChange(EMPTY_CHANGE)
    setEditedDate(undefined)
  }

  const allChangesScheduled = (): boolean => {
    const allDefaultChangesScheduled =
      scheduledChanges.includes('employeeLevel') && scheduledChanges.includes('salary') && scheduledChanges.includes('bonus')
    if (hasNationalContract) {
      return allDefaultChangesScheduled && scheduledChanges.includes('nationalReferenceLevel')
    } else if (countrySettings.hasGovernmentJobTitleReferenceLevel) {
      return allDefaultChangesScheduled && scheduledChanges.includes('governmentJobTitle')
    } else {
      return allDefaultChangesScheduled
    }
  }

  if (!currentContract.annualSalary || !currentContract.employeeLevel) {
    return (
      <PerFlexRow justify="space-between" align="middle" mb="xs" mr="s">
        <PerText appearance="heading" mb="xs" color="primary">
          {t(`appraisalMeeting.contract.changes.warning`)}
        </PerText>
      </PerFlexRow>
    )
  }

  const columns = [
    {
      key: 'validFrom',
      title: t('appraisalMeeting.contract.changes.table.from'),
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (
        a: Pick<Change, 'id' | 'validFrom' | 'changes' | 'status' | 'type' | 'updatedDate'>,
        b: Pick<Change, 'id' | 'validFrom' | 'changes' | 'status' | 'type' | 'updatedDate'>,
      ) => a.validFrom.localeCompare(b.validFrom),
      render: (rowData: Pick<Change, 'id' | 'validFrom' | 'changes' | 'status' | 'type' | 'updatedDate'>) =>
        rowData.validFrom && formatDateShort(rowData.validFrom, true),
    },
    {
      key: 'change',
      title: t('appraisalMeeting.contract.changes.table.change'),
      render: (rowData: Pick<Change, 'id' | 'validFrom' | 'changes' | 'status' | 'type' | 'updatedDate'>) => {
        const changeGovernmentJobTitleString: string | undefined = JSON.parse(rowData.changes).governmentJobTitle
        const changeGovernmentJobTitle: GovernmentJobTitleTinyDto | undefined =
          transformFromStringToGovernmentJobTitle(changeGovernmentJobTitleString)
        if (
          !governmentJobTitles ||
          !countrySettings.hasGovernmentJobTitleReferenceLevel ||
          !currentContract.contractGovernmentJobTitle ||
          !changeGovernmentJobTitle ||
          isGovernmentJobTitleValid(governmentJobTitles, changeGovernmentJobTitle)
        ) {
          return (
            <ContractChangeList
              raw={rowData.changes}
              user={user}
              nationalContractReferenceLevel={
                hasNationalContract && currentContract.nationalContractReferenceLevel
                  ? currentContract.nationalContractReferenceLevel.description
                  : undefined
              }
              isConclusion={isConclusion}
              isPreparation={isPreparation}
              contract={currentContract}
              careerPathData={careerPathData}
              hasCareerPath={!!careerPathEvaluation}
            />
          )
        } else {
          return (
            <PerFlexRow align="middle">
              <Col span={1}>
                <IconIndicator
                  icon="warning_fill"
                  iconSize={12}
                  background="error"
                  size="l"
                  tooltip={t('appraisalMeeting.contract.changes.table.tooltips.invalidGovernmentJobTitle')}
                />
              </Col>
              <Col>
                <ContractChangeList
                  raw={rowData.changes}
                  user={user}
                  nationalContractReferenceLevel={
                    hasNationalContract && currentContract.nationalContractReferenceLevel
                      ? currentContract.nationalContractReferenceLevel.description
                      : undefined
                  }
                  isConclusion={isConclusion}
                  isPreparation={isPreparation}
                  contract={currentContract}
                  careerPathData={careerPathData}
                  hasCareerPath={!!careerPathEvaluation}
                />
              </Col>
            </PerFlexRow>
          )
        }
      },
    },
    {
      key: 'actions',
      title: t('table.actions'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(2),
      render: (rowData: Pick<Change, 'id' | 'validFrom' | 'changes' | 'status' | 'type' | 'updatedDate'>) => {
        return (
          <PerTableActionButtons
            buttons={[
              {
                icon: 'edit_fill',
                color: 'primary',
                tooltip: t('tooltip.edit'),
                showOnlyWhen: !handleLowTag,
                onClick: () => {
                  setChange(transformToContractChangeObject(rowData))
                  setVisible(true)
                  toggleEdit(true)
                  setEditedDate(rowData.validFrom)
                },
                disabled: isDeleting,
              },
              {
                icon: 'delete_fill',
                color: 'error',
                tooltip: t('tooltip.delete'),
                onClick: async () => {
                  setChange(transformToContractChangeObject(rowData))
                  setVisible(true)
                  toggleEdit(true)
                  toggleDeleting(true)
                  setEditedDate(rowData.validFrom)
                },
                isProcessing: { active: isDeleting },
              },
            ]}
          />
        )
      },
    },
  ]

  const filterActionsCol = (column: { key: string }) => isContractChange || column.key !== 'actions'

  return (
    <>
      <ContractChangeModalWithValidationList
        meeting={meeting}
        currentContract={currentContract}
        change={change}
        visible={visible}
        closeModal={closeModal}
        datesOfCurrentChanges={datesOfCurrentChanges}
        careerPathEvaluation={careerPathEvaluation}
        scheduledChanges={scheduledChanges}
        isEdit={isEdit}
        isDeleting={isDeleting}
        isPreparation={isPreparation}
        editedDate={editedDate}
        hasNationalContractReference={hasNationalContract}
        governmentJobTitlesByHiringOffice={governmentJobTitles}
        countrySettings={countrySettings}
      />

      <PerFlexRow justify="space-between" align="middle" mb={isContractChange ? 'xs' : 's'} mr="s">
        <PerFlexRow align="middle">
          <PerText appearance="heading" color="primary">
            {t(`appraisalMeeting.contract.changes.tableHeadline`)}
          </PerText>
          {handleLowTag && tableData.length > 0 && (
            <>
              <PerSpacer ml="xs" />
              <IconIndicator
                icon="warning_fill"
                background="error"
                variant="clickable"
                size="l"
                tooltip={t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingContractChangesForLowEmployee')}
              />
            </>
          )}
        </PerFlexRow>
        {isContractChange && !noMoreUpdatesPossible && !allChangesScheduled() && (
          <PerButtonCircle
            icon="add_stroke"
            variant="pastel"
            color="success"
            onClick={() => {
              setChange(EMPTY_CHANGE)
              setVisible(true)
            }}
            disabled={handleLowTag}
            tooltip={handleLowTag ? t('appraisalMeeting.stepContractChanges.contractChangeButtonDisabled') : ''}
            data-e2e-add-contract-change
          />
        )}
      </PerFlexRow>

      {countrySettings.paymentsAndChangesDisclaimerVisible && (
        <PerText color="secondary" mb="s">
          {t(`appraisalMeeting.contract.changes.notOnFirstOfMonthDisclaimer`)}
        </PerText>
      )}

      <Table
        dataSource={tableData.map((change, index: number) => ({ ...change, key: index }))}
        columns={columns.filter((c) => filterActionsCol(c))}
        pagination={false}
        locale={{
          emptyText: isContractChange && (
            <PerEmpty
              info={t('appraisalMeeting.contract.changes.table.empty')}
              button={
                !handleLowTag && (
                  <PerButton
                    text={t('appraisalMeeting.contract.changes.action.createBtn')}
                    before="add_stroke"
                    color="success"
                    onClick={() => {
                      setChange(EMPTY_CHANGE)
                      setVisible(true)
                    }}
                    data-e2e-add-contract-change
                  />
                )
              }
            />
          ),
        }}
        data-e2e-contract-change-table
      />
    </>
  )
}
